import React, { useEffect, useState } from "react";
import { Modal, Box } from "@mui/material";
import useDriverTripLogs from "../../hooks/trips/useDriverTripLogs";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Spinner from "../../components/loader/Spinner";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import { LocationMarkerIcon } from "@heroicons/react/solid";
import L from "leaflet";
import { Button } from "../../components/ui";

// Fix for Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "/assets/markers/marker-icon-2x.png",
  iconUrl: "/assets/markers/marker-icon.png",
  shadowUrl: "/assets/markers/marker-shadow.png",
});

// Component to ensure all points are visible on the map when rendered
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [bounds, map]);
  return null;
}

// Component for recenter button
function RecenterButton({ position }) {
  const map = useMap();

  const handleRecenter = () => {
    if (position) {
      // Get the current zoom level and use it to maintain the same zoom
      const currentZoom = map.getZoom();
      map.setView(position, currentZoom);
    }
  };

  return (
    <div className="leaflet-top leaflet-left" style={{ marginTop: "80px" }}>
      <div className="leaflet-control leaflet-bar">
        <Button
          onClick={handleRecenter}
          className="bg-white shadow-md hover:bg-gray-100"
          title="Recenter to start point"
          variant={"ghost"}
          size={"icon"}
        >
          <LocationMarkerIcon className="h-4 w-4 text-gray-700" />
        </Button>
      </div>
    </div>
  );
}

const TripLogsModal = ({
  open,
  onClose,
  trip,
  company_employee_id,
  driverDetails: propDriverDetails,
}) => {
  const tripId = trip?.trip_id;

  const { tripLogsData, isLoading, paramsObject, setGetListParams } =
    useDriverTripLogs(tripId, company_employee_id);
  const [tripCoordinates, setTripCoordinates] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [driverDetails, setDriverDetails] = useState(propDriverDetails || {});

  // Update parameters when trip_id changes
  useEffect(() => {
    if (tripId && tripId !== paramsObject.trip_id) {
      setGetListParams({
        ...paramsObject,
        trip_id: tripId || "",
      });
    }
  }, [tripId, paramsObject, setGetListParams]);

  useEffect(() => {
    if (tripLogsData && tripLogsData.data) {
      // Filter valid coordinates and map to [lat, long] format
      const coordinates = tripLogsData.data
        .filter((log) => log.location_lat && log.location_long)
        .map((log) => [
          parseFloat(log.location_lat),
          parseFloat(log.location_long),
        ]);
      // Deduplicate coordinates
      const uniqueCoordinates = coordinates.filter(
        (coord, index, self) =>
          index === self.findIndex(
            (t) => t[0] === coord[0] && t[1] === coord[1]
          )
      );
      // Store the (unique) coordinates
      setTripCoordinates(uniqueCoordinates);

      // Without unique coordinates
      // const coordinates = tripLogsData.data
      //   .filter((log) => log.location_lat && log.location_long)
      //   .map((log) => [
      //     parseFloat(log.location_lat),
      //     parseFloat(log.location_long),
      //   ]);
      // setTripCoordinates(coordinates);

      if (uniqueCoordinates.length > 0) {
        const latLngs = uniqueCoordinates.map((coord) =>
          L.latLng(coord[0], coord[1])
        );
        setBounds(L.latLngBounds(latLngs));
      }

      if (!propDriverDetails) {
        setDriverDetails(tripLogsData.driver_details || {});
      }
    }
  }, [tripLogsData, propDriverDetails]);

  const startPoint = tripCoordinates[0];
  const endPoint = tripCoordinates[tripCoordinates.length - 1];

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-35%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          width: "80%",
          maxWidth: 900,
          maxHeight: "90vh",
          overflow: "auto",
          borderRadius: "8px",
          p: 4,
        }}
      >
        <Button
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          onClick={onClose}
          variant={"ghost"}
          size={"icon"}
        >
          ✕
        </Button>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-4">
                {driverDetails?.name ||
                  `${driverDetails?.first_name || ""} ${
                    driverDetails?.last_name || ""
                  }` ||
                  "Driver Trip Details"}
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold">Trip Start:</h3>
                  <p>
                    {trip?.start_date
                      ? new Date(trip.start_date * 1000).toLocaleString()
                      : "-"}
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold">Trip End:</h3>
                  <p>
                    {trip?.end_date
                      ? new Date(trip.end_date * 1000).toLocaleString()
                      : "-"}
                  </p>
                </div>
              </div>
            </div>

            <div style={{ height: "500px", width: "100%" }}>
              {tripCoordinates.length > 0 ? (
                <MapContainer
                  style={{ height: "100%", width: "100%" }}
                  center={startPoint || [20.5937, 78.9629]} // Default to India center
                  zoom={13}
                >
                  {bounds && <FitBounds bounds={bounds} />}
                  <RecenterButton position={startPoint} />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {/* Draw path between points */}
                  <Polyline
                    positions={tripCoordinates}
                    color="blue"
                    weight={4}
                  />

                  {/* Special markers for start and end points */}
                  {startPoint && (
                    <Marker
                      position={startPoint}
                      icon={
                        new L.Icon({
                          iconUrl: "/assets/markers/marker-icon-2x-green.png",
                          shadowUrl: "/assets/markers/marker-shadow.png",
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41],
                        })
                      }
                    />
                  )}

                  {endPoint && endPoint !== startPoint && (
                    <Marker
                      position={endPoint}
                      icon={
                        new L.Icon({
                          iconUrl: "/assets/markers/marker-icon-2x-red.png",
                          shadowUrl: "/assets/markers/marker-shadow.png",
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41],
                        })
                      }
                    />
                  )}

                  {/* Intermediate checkpoints */}
                  {/* 
                  {tripCoordinates.slice(1, -1).map((position, idx) => (
                    <Marker key={idx} position={position} />
                  ))}
                  */}
                </MapContainer>
              ) : (
                <div className="flex justify-center items-center h-full bg-gray-100">
                  <PrimaryLoader />
                </div>
              )}
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default TripLogsModal;
